import { AssetCameraKey, AssetId, CameraId } from "@visioni/a3s-schema";
import type { CameraAssetGroup } from "../client-messages/vehicles";

/**
 * What to call a camera: its A3S nickname, else `formatCameraLabel`, else the
 * raw camera key, so the name is never blank. This follows the app's naming
 * rule (nickname, then label), falling back to the key as the app does in
 * places such as cameras/[cameraKey].native.tsx.
 *
 * `cameras` is the cameras response; a nickname that is blank or whitespace
 * counts as none.
 */
export function cameraName(assetId: string, cameraId: string, cameras: readonly CameraAssetGroup[]): string {
  const cameraKey = `${assetId}@${cameraId}`;
  const nickname = cameras.find((c) => c.assetId === assetId)?.cameraNicknames?.[cameraId]?.trim();
  return nickname || formatCameraLabel(cameraKey) || cameraKey;
}

/**
 * Mirrors `formatCameraLabel` in `apps/a3s-app/src/utils/key-parser.ts`, which
 * features can't import (ADR-0026); keep the two identical. "CAM_11558 -
 * Camera A" for a valid key, "" for a legacy-app asset (whose id can't be
 * shown), and the key itself when it doesn't parse.
 */
export function formatCameraLabel(cameraKey: string): string {
  try {
    const key = AssetCameraKey.fromString(cameraKey);
    const asset = AssetCameraKey.asset(key);
    const cameraId = CameraId.toString(AssetCameraKey.camera(key))
      .replace(/[-_]+/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    if (AssetId.source(asset) === "legacy_app") return "";
    return `${AssetId.unique(asset)} - ${cameraId}`;
  } catch {
    return cameraKey;
  }
}
